import Marquee from "react-fast-marquee";
import { Link } from "react-router-dom";
import { Award, ArrowRight } from "lucide-react";
import { useSiteSettings } from "@/data/hooks";

const FALLBACK_LOGOS = [
  { name: "SanMar", url: "/api/media/1ae0e94a-5fb3-4997-ae5f-51323f4df8fa" },
  { name: "Towel Specialties", url: "/api/media/2bab1d14-76c6-4d23-bfd9-951a62e3e62e" },
  { name: "Suntex", url: "/api/media/eec106e1-8ba0-4e74-a0f0-9c3125795483" },
  { name: "Midnite Snax", url: "/api/media/2b8f5944-e0d1-4ce5-bdc3-803c12845f60" },
  { name: "LarLu", url: "/api/media/aaa78c86-bdc5-4847-9983-3b7776837bc8" },
  { name: "Cap America", url: "/api/media/839d041a-dec0-45f7-b59e-8be8f048208e" },
  { name: "HIT Promo", url: "/api/media/387e029c-dc4c-45e5-84fb-2c2c1eb7f683" },
  { name: "Gill Line", url: "/api/media/2a4af691-ba5c-4722-a363-e4e090cbf88b" },
  { name: "Ball Pro", url: "/api/media/28f4a6d6-9498-4de5-ba34-9ed7c5672132" },
  { name: "Bag Makers", url: "/api/media/776d91c8-1725-45ea-923e-ae1993278d63" },
  { name: "3M Promo", url: "/api/media/7f1c262e-2ccc-4841-873e-e115e0b14297" },
  { name: "WOW Line", url: "/api/media/62f01230-0652-4440-9f57-f478008c4b7d" },
  { name: "Kanata Group", url: "/api/media/8e6d5efa-9022-4b53-a0e7-8c5efd8e62ae" },
  { name: "Strideline", url: "/api/media/c4e0b1b5-e648-4641-9f7d-0bcdca379806" },
  { name: "PowerStick", url: "/api/media/4e83d9da-0d8e-4e85-84db-b12b5ac6a514" },
  { name: "Starline", url: "/api/media/888db964-4c75-4d36-bfb4-fb2793ebfa3c" },
  { name: "Vantage", url: "/api/media/b38911f8-95d8-48e5-994c-5451a5e1b270" },
  { name: "Outdoor Cap", url: "/api/media/091c067c-87cb-4506-be1a-a64c10fc057d" },
  { name: "Logomark", url: "/api/media/39787fa8-da10-4f36-8505-715eae0f3e9a" },
  { name: "Tekweld", url: "/api/media/8f1e6bdd-190f-40dd-825a-0dd85c454e58" },
];

export default function PartnerLogos() {
  const { settings } = useSiteSettings();
  // Admin-curated suppliers (if any) take precedence over the bundled list.
  const adminSuppliers = (settings.suppliers || []).filter((s) => s.name && s.logo_url);
  const logos = adminSuppliers.length
    ? adminSuppliers.map((s) => ({ name: s.name, url: s.logo_url, href: s.url || null }))
    : FALLBACK_LOGOS;

  return (
    <section
      data-testid="partner-logos-section"
      className="py-10 lg:py-14 bg-white relative"
    >
      {/* Subtle gradient border-top strip to lift the section visually */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-sky-200 to-transparent" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-6 lg:mb-8">
        <div className="text-center">
          <span
            data-testid="partner-logos-eyebrow"
            className="inline-flex items-center gap-1.5 text-xs font-semibold tracking-[0.2em] uppercase text-sky-600 bg-sky-50 border border-sky-200 rounded-full px-3 py-1"
          >
            <Award className="h-3 w-3" />
            The Elite Supplier Program
          </span>
          <h2
            data-testid="partner-logos-heading"
            className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900"
          >
            <Link
              to="/exchange/elite-supplier"
              data-testid="partner-logos-heading-link"
              className="inline-block hover:text-sky-600 transition-colors"
            >
              Antera&apos;s Elite Suppliers
            </Link>
          </h2>
          <p className="mt-3 text-sm lg:text-base text-slate-500 max-w-2xl mx-auto leading-relaxed">
            Preferred supplier partners delivering deep PromoStandards integration, real-time data, and exclusive commercial benefits to the Antera community.
          </p>
          <Link
            to="/exchange/elite-supplier"
            data-testid="partner-logos-learn-link"
            className="btn-premium inline-flex items-center gap-2 mt-6 rounded-full bg-sky-600 hover:bg-sky-700 text-white text-sm font-bold px-6 py-3 shadow-lg shadow-sky-600/25 transition group"
          >
            Learn about the program
            <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
          </Link>
        </div>
      </div>

      <Marquee speed={35} gradient gradientColor="#ffffff" gradientWidth={60} pauseOnHover>
        {logos.map((logo) => {
          const isLink = !!logo.href;
          const InnerImage = (
            <div className="w-36 h-36 sm:w-48 sm:h-48 md:w-56 md:h-56 lg:w-64 lg:h-64 flex items-center justify-center p-3 sm:p-4 rounded-lg transition-transform hover:scale-105">
              <img
                src={logo.url}
                alt={logo.name}
                className="marquee-logo max-w-full max-h-full object-contain transition-opacity hover:opacity-80"
                data-testid={`logo-${logo.name.toLowerCase().replace(/\s/g, "-")}`}
              />
            </div>
          );
          if (isLink) {
            return (
              <a
                key={logo.name}
                href={logo.href}
                target="_blank"
                rel="noopener noreferrer"
                className="mx-3 sm:mx-4 md:mx-5 flex items-center justify-center cursor-pointer"
                aria-label={logo.name}
              >
                {InnerImage}
              </a>
            );
          }
          return (
            <span
              key={logo.name}
              className="mx-3 sm:mx-4 md:mx-5 flex items-center justify-center"
              aria-label={logo.name}
            >
              {InnerImage}
            </span>
          );
        })}
      </Marquee>
    </section>
  );
}
